import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select2 from "../../Components/Accounting/Select2";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// Resilient renderer: tables for arrays-of-rows, key/value for objects, plain otherwise.
const SmartRender = ({ data }) => {
  if (data === null || data === undefined) return <div className="tx-muted">No data.</div>;
  if (Array.isArray(data)) {
    if (data.length === 0) return <div className="tx-muted">No rows.</div>;
    if (typeof data[0] !== "object") return <div>{data.map((v, i) => <span key={i} className="badge bg-light text-dark me-1">{fmt(v)}</span>)}</div>;
    const cols = Object.keys(data[0]);
    return (
      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-0">
          <thead><tr>{cols.map((c) => <th key={c} className="tx-12 text-capitalize">{c.replace(/_/g, " ")}</th>)}</tr></thead>
          <tbody>{data.map((row, i) => <tr key={i}>{cols.map((c) => <td key={c} className="tx-12">{fmt(row[c])}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
  if (typeof data === "object") {
    return (
      <table className="table table-sm mb-0">
        <tbody>
          {Object.entries(data).map(([k, v]) => (
            <tr key={k}>
              <td className="fw-semibold tx-12 text-capitalize" style={{ width: "38%" }}>{k.replace(/_/g, " ")}</td>
              <td className="tx-12">{Array.isArray(v) || (v && typeof v === "object") ? <SmartRender data={v} /> : fmt(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return <div>{fmt(data)}</div>;
};

const SECTIONS = [
  { key: "kpi", label: "KPI Ratios", icon: "fa-gauge-high", fn: "mgKpiRatios" },
  { key: "cm", label: "Contribution Margin", icon: "fa-layer-group", fn: "mgContributionMargin" },
  { key: "be", label: "Break-Even", icon: "fa-scale-balanced", fn: "mgBreakEven" },
  { key: "var", label: "Budget Variance", icon: "fa-chart-column", fn: "mgVariance" },
  { key: "seg", label: "Segment P&L", icon: "fa-diagram-project", fn: "mgSegmentPl" },
  { key: "cost", label: "Cost Classification", icon: "fa-tags", fn: "mgCostReport" },
];

const kpiColor = (status) => ({ green: "#10b981", amber: "#f59e0b", red: "#ef4444", grey: "#6b7280" }[status] || "#6366f1");

const ManagerialAccounting = () => {
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [section, setSection] = useState("kpi");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchFY(); }, []);
  useEffect(() => { if (selectedFY) load(section, selectedFY); }, [selectedFY, section]);

  const fetchFY = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1 || y.is_current === "1");
      setSelectedFY(String(cur ? cur.id : res.details?.[0]?.id || ""));
    }
  };

  const load = async (secKey, fyId) => {
    const sec = SECTIONS.find((s) => s.key === secKey);
    if (!sec) return;
    setLoading(true); setResult(null);
    const res = await ajaxAccounting[sec.fn]({ financial_year_id: parseInt(fyId) });
    if (res.status === "OK") setResult(res.details);
    else { setResult(null); if (res.message) toast.error(res.message); }
    setLoading(false);
  };

  const fyOptions = financialYears.map((y) => ({ value: String(y.id), label: y.name + (y.is_current ? " (Current)" : "") }));
  const kpis = section === "kpi" && result && Array.isArray(result.kpis) ? result.kpis : null;

  return (
    <AppContainer
      title="Managerial Accounting"
      previousPageTitle="Accounting"
      description="Decision-support analytics: KPI ratios, contribution margin, break-even, budget variance, segment profitability and cost classification for the selected financial year."
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select2 options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select Financial Year" />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {SECTIONS.map((s) => (
          <button key={s.key} className={`btn btn-sm ${section === s.key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setSection(s.key)}>
            <i className={`fa-solid ${s.icon} me-1`}></i>{s.label}
          </button>
        ))}
      </div>

      <div className="card custom-card p-3">
        <h6 className="fw-semibold mb-3">{SECTIONS.find((s) => s.key === section)?.label}</h6>
        {loading ? (
          <div className="text-center py-5 tx-muted">Loading…</div>
        ) : kpis ? (
          <div className="row">
            {kpis.map((k, i) => (
              <div key={i} className="col-xl-3 col-lg-4 col-sm-6 mb-3">
                <div className="card custom-card" style={{ borderLeft: `4px solid ${kpiColor(k.status)}`, padding: "16px" }}>
                  <div className="tx-11 tx-uppercase fw-semibold mb-1" style={{ color: kpiColor(k.status) }}>{k.label}</div>
                  <div className="fw-bold tx-20">{k.value === null ? "—" : fmt(k.value)}{k.unit === "%" ? "%" : ""}</div>
                  {k.benchmark && <div className="tx-11 tx-muted mt-1">Target: {k.benchmark}</div>}
                </div>
              </div>
            ))}
            {kpis.length === 0 && <div className="tx-muted">No KPI data for this period.</div>}
          </div>
        ) : result ? (
          <SmartRender data={result} />
        ) : (
          <div className="text-center py-5 tx-muted">No data for this period. Post some journal entries first.</div>
        )}
      </div>
    </AppContainer>
  );
};

export default ManagerialAccounting;
